// Which carriers can be offered for which coverage line, for this client.
//
// Shared by quote mode's step 4 and the RFP's Recipients step. Both ask the same
// three questions of a carrier and must answer them identically, or a carrier
// offered on one screen is silently missing from the other.
//
// In the RFP's GA mode this runs on top of the GA's own roster: a firm may have
// an appointment with a carrier that still does not write business where the
// client is, and that carrier must not be offerable.

import { useMemo, useCallback } from "react";
import { normalizePlanType } from "@shared/schema";
import type { Carrier } from "@shared/schema";
import { useQuoteWizardContext } from "../QuoteWizardContext";

export function useCarrierAvailability() {
  const { quoteData, carriers, quotableCarriers, isRFPMode } = useQuoteWizardContext();

  const entityState = quoteData.selectedEntity?.state || null;

  // Coverage types each carrier can actually be quoted for in this state
  // (library plans offered there with rates on file), by carrier id.
  const quotableTypesByCarrier = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const qc of quotableCarriers || []) {
      map.set(qc.id, new Set((qc.quotableTypes || []).map((t) => normalizePlanType(t))));
    }
    return map;
  }, [quotableCarriers]);

  // Mock carrier (dental/vision/life demo data) is served by /api/mock-plans,
  // not the library, so it stays available for those types.
  const isMockCarrier = (carrier: Carrier) => carrier.wordBrownId === "MOCK-CARRIER-001";

  // Quote mode needs real data (library plans + rates in the entity's state).
  // RFP mode only needs somebody to send to, so it falls back to the carrier's
  // own state list.
  const isCarrierQuotable = useCallback(
    (carrier: Carrier, coverageType: string): boolean => {
      const normalizedCoverage = normalizePlanType(coverageType);
      if (isMockCarrier(carrier)) return true;
      if (!isRFPMode && entityState) {
        return quotableTypesByCarrier.get(carrier.id)?.has(normalizedCoverage) ?? false;
      }
      if (!entityState) return true;
      if (!carrier.availableStates || carrier.availableStates.length === 0) return true;
      return carrier.availableStates.includes(entityState);
    },
    [entityState, isRFPMode, quotableTypesByCarrier],
  );

  /** The RFP's coverage lines this carrier can be asked about. */
  const getCarrierCoverageTypes = useCallback(
    (carrier: Carrier): string[] => {
      const carrierTypes = carrier?.types || [];
      return quoteData.coverageTypes.filter((coverageType) => {
        const normalizedCoverage = normalizePlanType(coverageType);
        const supportsType = carrierTypes.some(
          (type: string) => normalizePlanType(type) === normalizedCoverage,
        );
        return supportsType && isCarrierQuotable(carrier, coverageType);
      });
    },
    [quoteData.coverageTypes, isCarrierQuotable],
  );

  /**
   * Every active carrier that writes this line, before the state question. For
   * screens that grey the unavailable ones rather than dropping them.
   */
  const getCarriersForLine = useMemo(() => {
    return (coverageType: string): Carrier[] => {
      if (!Array.isArray(carriers) || carriers.length === 0) return [];
      const normalizedCoverageType = normalizePlanType(coverageType);
      return carriers
        .filter((carrier: Carrier) => carrier.isActive !== false)
        .filter((carrier: Carrier) =>
          (carrier?.types || []).some(
            (type: string) => normalizePlanType(type) === normalizedCoverageType,
          ),
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    };
  }, [carriers]);

  const getAvailableCarriers = useMemo(() => {
    return (coverageType: string): Carrier[] =>
      getCarriersForLine(coverageType).filter((carrier) =>
        isCarrierQuotable(carrier, coverageType),
      );
  }, [getCarriersForLine, isCarrierQuotable]);

  const carrierById = useMemo(
    () => new Map((carriers || []).map((carrier: Carrier) => [carrier.id, carrier])),
    [carriers],
  );

  /**
   * selectedCarriers rebuilt from a flat carrier list. Step 5 and the RFP detail
   * screens read that field and know nothing about GAs, so GA mode has to keep
   * writing it as the roll-up of whatever was asked.
   */
  const toSelectedCarriers = useCallback(
    (carrierIds: number[]): Record<string, number[]> => {
      const selected: Record<string, number[]> = {};
      for (const coverageType of quoteData.coverageTypes) {
        selected[coverageType] = carrierIds.filter((carrierId) => {
          const carrier = carrierById.get(carrierId);
          return !!carrier && getCarrierCoverageTypes(carrier).includes(coverageType);
        });
      }
      return selected;
    },
    [quoteData.coverageTypes, carrierById, getCarrierCoverageTypes],
  );

  return {
    entityState,
    isCarrierQuotable,
    getCarrierCoverageTypes,
    getCarriersForLine,
    getAvailableCarriers,
    carrierById,
    toSelectedCarriers,
  };
}
